import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useCallback } from "react";
import { useTopology } from "@/store/site";

/**
 * Layer 2 of the app's three-layer persistence rule (PERS-11; see README):
 *
 *   1. URL search params — WHAT you are looking at (entity, window, series,
 *      filters). Shareable; written replace:true; validated late.
 *   2. ems-ui-prefs (this store, localStorage) — HOW you like it
 *      (granularities, scopes, window presets, theme-adjacent view prefs,
 *      last zone/site). Keyed per site so multi-site operators don't bleed
 *      preferences across plants. Re-validate against topology on read.
 *   3. sessionStorage — UNSAVED WORK (schedule drafts), restored with an
 *      explicit banner.
 *
 * localStorage works inside the ThingsBoard iframe (allow-same-origin is
 * set; partitioned per top-level site at worst — still stable across
 * dashboard reloads, which is exactly what DOCS-02 needs).
 *
 * Hydration precedence everywhere: URL → prefs → hardcoded default.
 */
interface UiPrefsState {
  /** prefs[siteId][prefKey] — JSON-serialisable view preferences. */
  prefs: Record<string, Record<string, unknown>>;
  setPref: (siteId: string, prefKey: string, value: unknown) => void;
}

export const useUiPrefsStore = create<UiPrefsState>()(
  persist(
    (set) => ({
      prefs: {},
      setPref: (siteId, prefKey, value) =>
        set((s) => ({
          prefs: {
            ...s.prefs,
            [siteId]: { ...s.prefs[siteId], [prefKey]: value },
          },
        })),
    }),
    { name: "ems-ui-prefs" },
  ),
);

/**
 * Per-site, per-key view preference. `prefKey` is "<page>.<pref>" by
 * convention (e.g. "overview.powerWindow", "revenue.gran", "market.zone").
 *
 * The raw stored value is returned as-is when present — consumers MUST
 * re-validate values that reference topology (unit ids, zone names) before
 * use, since the topology may have changed since the pref was written.
 */
export function useUiPref<T>(prefKey: string, fallback: T): [T, (v: T) => void] {
  const siteId = useTopology().id;
  const stored = useUiPrefsStore((s) => s.prefs[siteId]?.[prefKey]);
  const setPref = useUiPrefsStore((s) => s.setPref);
  const set = useCallback((v: T) => setPref(siteId, prefKey, v), [setPref, siteId, prefKey]);
  return [(stored === undefined ? fallback : (stored as T)), set];
}

/** Non-hook accessor for imperative code paths (store init, palette commands). */
export function readUiPref<T>(siteId: string, prefKey: string, fallback: T): T {
  const stored = useUiPrefsStore.getState().prefs[siteId]?.[prefKey];
  return stored === undefined ? fallback : (stored as T);
}

/**
 * Optional per-site "home view" (F23): the route a fresh "/" load lands on
 * when neither return-to-where-you-were nor an admin ?mode= deep link
 * applies. Set/cleared from the CommandPalette; honoured in RootRedirect
 * (App.tsx) AFTER those two, BEFORE the hardcoded /overview default.
 * Uses the existing "<page>.<pref>" key convention.
 */
export const HOME_VIEW_PREF = "home.view";
